// Versão: 2.03
// Data..: 2026-09-24
// Tela de login com painel de marca + troca de senha no 1º acesso + código MFA (TOTP).
// v2.03: MFA obrigatório — etapa de configuração do app autenticador no login
// (mostra a chave/QR e confirma o primeiro código) quando o usuário ainda não tem MFA.

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, type LoginOutcome } from '../context/AuthContext.js';
import { useTheme } from '../context/ThemeContext.js';
import { totpUri } from '../lib/auth.js';

export function LoginPage() {
  const { signIn } = useAuth();
  const { theme, toggle } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Etapa de definição de nova senha (primeiro acesso).
  const [needNewPassword, setNeedNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [finalize, setFinalize] = useState<((pwd: string) => Promise<LoginOutcome>) | null>(null);
  // Etapa de MFA (código do app autenticador).
  const [needTotp, setNeedTotp] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [finalizeTotp, setFinalizeTotp] = useState<((code: string) => Promise<void>) | null>(null);
  // Etapa de configuração obrigatória de MFA (usuário sem app autenticador).
  const [setupSecret, setSetupSecret] = useState<string | null>(null);
  const [finalizeSetup, setFinalizeSetup] = useState<((code: string) => Promise<void>) | null>(null);

  // Direciona a UI conforme o desfecho do login (nova senha, MFA ou configurar MFA).
  // status 'ok' não faz nada aqui: o App redireciona ao detectar o usuário.
  const applyOutcome = (outcome: LoginOutcome) => {
    if (outcome.status === 'new_password_required') {
      setFinalize(() => outcome.setNewPassword);
      setNeedNewPassword(true);
    } else if (outcome.status === 'totp_required') {
      setFinalizeTotp(() => outcome.submitTotp);
      setNeedTotp(true);
    } else if (outcome.status === 'totp_setup_required') {
      setSetupSecret(outcome.secret);
      setFinalizeSetup(() => outcome.confirmSetup);
      // Sai de eventuais etapas anteriores (ex.: nova senha) para mostrar a config.
      setNeedNewPassword(false);
      setNeedTotp(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      applyOutcome(await signIn(email, password));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha no login');
    } finally {
      setLoading(false);
    }
  };

  // Confirma o primeiro código do app autenticador na configuração obrigatória de MFA.
  const submitSetup = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!finalizeSetup) return;
    setLoading(true);
    try {
      await finalizeSetup(totpCode.trim());
      // Sucesso: o App redireciona ao detectar o usuário autenticado.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  const submitTotp = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!finalizeTotp) return;
    setLoading(true);
    try {
      await finalizeTotp(totpCode.trim());
      // Sucesso: o App redireciona ao detectar o usuário autenticado.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 10) {
      setError('A nova senha deve ter ao menos 10 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('As senhas não conferem.');
      return;
    }
    if (!finalize) return;
    setLoading(true);
    try {
      // Definir a senha pode encadear a configuração de MFA: aplica o próximo passo.
      applyOutcome(await finalize(newPassword));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao definir a senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Painel de marca */}
      <div
        className="hide-mobile"
        style={{
          flex: 1,
          background:
            'radial-gradient(1000px 480px at 15% 0%, rgba(255,255,255,0.18), transparent 60%), linear-gradient(135deg, #0088cc 0%, #6c3ce0 100%)',
          color: '#fff',
          display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '56px',
          position: 'relative', overflow: 'hidden',
        }}
      >
        <div style={{ maxWidth: 460, position: 'relative', zIndex: 1 }}>
          <img src="/logo-dark.png" alt="Makweb" style={{ height: 42, width: 'auto', marginBottom: 30 }} />
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 33, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15, marginBottom: 16 }}>
            Proteção e atendimento de nível enterprise
          </div>
          <p style={{ fontSize: 16, opacity: 0.85, lineHeight: 1.65 }}>
            Central de suporte da Makweb: abra e acompanhe chamados por múltiplos canais,
            com base de conhecimento, filas, SLA e alertas.
          </p>
          <div className="mt-5 flex flex-col gap-3" style={{ fontSize: 14 }}>
            {['Web, e-mail, WhatsApp e chat do site', 'Base de conhecimento pública e privada', 'Agendamento, tarefas e alertas automáticos'].map((f) => (
              <div key={f} className="flex items-center gap-2" style={{ opacity: 0.92 }}>
                <span>✓</span> {f}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Formulário */}
      <div style={{ width: 460, maxWidth: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ width: '100%', maxWidth: 360 }} className="fade-in">
          <div className="flex items-center justify-between mb-4">
            <h1 className="mb-0">{setupSecret ? 'Configurar verificação em duas etapas' : needTotp ? 'Verificação em duas etapas' : needNewPassword ? 'Definir nova senha' : 'Entrar'}</h1>
            <button className="btn btn-icon btn-ghost" onClick={toggle} type="button">
              {theme === 'light' ? '🌙' : '☀️'}
            </button>
          </div>

          {setupSecret ? (
            <>
              <p className="text-muted">
                Sua conta exige verificação em duas etapas. Configure um app autenticador
                (Google Authenticator, Authy, etc.) para continuar.
              </p>
              <div className="field mt-3">
                <label className="label">1. Adicione esta chave no app (ou toque no link)</label>
                <input
                  className="input"
                  readOnly
                  value={setupSecret}
                  style={{ fontFamily: 'monospace', letterSpacing: '0.1em' }}
                  onFocus={(e) => e.target.select()}
                />
                <div className="text-xs mt-1">
                  <a href={totpUri(setupSecret, email)}>Abrir no app autenticador ↗</a>
                </div>
              </div>
              <form onSubmit={submitSetup} className="mt-2">
                <div className="field">
                  <label className="label" htmlFor="setup-code">2. Digite o código de 6 dígitos gerado pelo app</label>
                  <input
                    id="setup-code"
                    className="input"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    required
                    autoFocus
                    style={{ letterSpacing: '0.3em', fontSize: 18, textAlign: 'center' }}
                  />
                </div>
                {error && (
                  <div className="mb-4" style={{ color: 'var(--danger)', background: 'var(--danger-soft)', padding: '10px 12px', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
                    {error}
                  </div>
                )}
                <button className="btn btn-primary btn-lg w-full" type="submit" disabled={loading || totpCode.length < 6}>
                  {loading ? 'Ativando...' : 'Ativar e entrar'}
                </button>
              </form>
            </>
          ) : needTotp ? (
            <>
              <p className="text-muted">Digite o código de 6 dígitos do seu app autenticador.</p>
              <form onSubmit={submitTotp} className="mt-4">
                <div className="field">
                  <label className="label" htmlFor="totp">Código</label>
                  <input
                    id="totp"
                    className="input"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    required
                    autoFocus
                    style={{ letterSpacing: '0.3em', fontSize: 18, textAlign: 'center' }}
                  />
                </div>
                {error && (
                  <div className="mb-4" style={{ color: 'var(--danger)', background: 'var(--danger-soft)', padding: '10px 12px', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
                    {error}
                  </div>
                )}
                <button className="btn btn-primary btn-lg w-full" type="submit" disabled={loading || totpCode.length < 6}>
                  {loading ? 'Verificando...' : 'Verificar e entrar'}
                </button>
              </form>
            </>
          ) : !needNewPassword ? (
            <>
              <p className="text-muted">Acesse sua conta de atendimento.</p>
              <form onSubmit={submit} className="mt-4">
                <div className="field">
                  <label className="label" htmlFor="email">E-mail</label>
                  <input id="email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className="field">
                  <label className="label" htmlFor="password">Senha</label>
                  <input id="password" className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                </div>
                {error && (
                  <div className="mb-4" style={{ color: 'var(--danger)', background: 'var(--danger-soft)', padding: '10px 12px', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
                    {error}
                  </div>
                )}
                <button className="btn btn-primary btn-lg w-full" type="submit" disabled={loading}>
                  {loading ? 'Entrando...' : 'Entrar'}
                </button>
              </form>
              <div className="text-center mt-4 text-sm">
                <Link to="/portal">← Ir para o portal público</Link>
              </div>
            </>
          ) : (
            <>
              <p className="text-muted">Primeiro acesso: crie uma senha definitiva para sua conta.</p>
              <form onSubmit={submitNewPassword} className="mt-4">
                <div className="field">
                  <label className="label" htmlFor="new-password">Nova senha</label>
                  <input id="new-password" className="input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Mínimo 10 caracteres" required autoFocus />
                </div>
                <div className="field">
                  <label className="label" htmlFor="confirm-password">Confirmar nova senha</label>
                  <input id="confirm-password" className="input" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                </div>
                {error && (
                  <div className="mb-4" style={{ color: 'var(--danger)', background: 'var(--danger-soft)', padding: '10px 12px', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
                    {error}
                  </div>
                )}
                <button className="btn btn-primary btn-lg w-full" type="submit" disabled={loading}>
                  {loading ? 'Salvando...' : 'Definir senha e entrar'}
                </button>
              </form>
              <div className="text-center mt-4 text-sm">
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => { setNeedNewPassword(false); setError(''); setNewPassword(''); setConfirmPassword(''); }}>← Voltar</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
